import { LoadingSpinner } from '@ad-lucy/components/shared/LoadingSpinner';
import type { CreativesConfig } from '@ad-lucy/config/creatives';
import { type GlobalConfig, useGlobalConfig } from '@ad-lucy/context/adData';
import { AdProviderBundle } from '@ad-lucy/context/adData/AdProviderBundle';
import { useCreative } from '@ad-lucy/hooks/creatives/useCreative';
import { withLiveRendering } from '@ad-lucy/services/LiveRendering';
import { defaultTheme } from '@ad-lucy/styled';
import { BootstrapUtils, type StateUpdater } from '@ad-lucy/utils';
import { StrictMode, Suspense, useEffect, useState } from 'react';

function App(props: Readonly<AppProps>) {
  const {
    contentUrl,
    globalUrl,
    themeUrl,
    customCssUrl,
    fontsUrl,
    storesUrl,
    configModifier = (config) => config,
  } = props;

  const [globalConfig, setGlobalConfig] = useState<GlobalConfig>();
  const { config, content: Content } = useCreative(globalConfig);

  useEffect(() => {
    BootstrapUtils.mountPublicCSSFiles({
      themeUrl,
      customCssUrl,
      fontsUrl,
    }).catch((err) => console.error('Error mounting CSS files:', err));
  }, [themeUrl, customCssUrl, fontsUrl]);

  return (
    <StrictMode>
      <AdProviderBundle
        config={configModifier(config as CreativesConfig)}
        contentUrl={contentUrl}
        globalUrl={globalUrl}
        theme={defaultTheme}
        storesUrl={storesUrl}
      >
        <GlobalConfigSetter set={setGlobalConfig} />
        <Suspense fallback={<LoadingSpinner />}>
          <Content />
        </Suspense>
      </AdProviderBundle>
    </StrictMode>
  );
}

const GlobalConfigSetter = ({ set }: { set: StateUpdater<GlobalConfig | undefined> }) => {
  const { data } = useGlobalConfig();
  useEffect(() => {
    set(data);
  }, [data, set]);
  return null;
};

export default withLiveRendering(App);
